import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const supported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export function PushToggle() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setEnabled(!!sub))
      .catch(() => {});
  }, []);

  if (!supported()) return null;

  async function enable() {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications blocked. Enable them in your browser settings."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.key) { toast.error("Push not configured on the server."); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setEnabled(true);
      toast.success("Notifications enabled on this device.");
    } catch (e) {
      toast.error("Could not enable notifications on this device.");
    } finally { setBusy(false); }
  }

  async function disable() {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); await sub.unsubscribe(); }
      setEnabled(false);
      toast.success("Notifications turned off on this device.");
    } catch (e) {
      toast.error("Could not turn off notifications.");
    } finally { setBusy(false); }
  }

  return (
    <Button variant="ghost" disabled={busy} onClick={enabled ? disable : enable} data-testid="push-toggle"
      className="w-full justify-start text-teal-100 hover:bg-teal-900/60 hover:text-white">
      {enabled ? <><BellOff className="mr-2 h-4 w-4" /> Turn Off Notifications</> : <><Bell className="mr-2 h-4 w-4" /> Enable Notifications</>}
    </Button>
  );
}
